import { useEffect, useMemo, useState } from "react";
import {
  getEmployeeAnnouncements,
  markAnnouncementRead,
} from "../api/instateamAnnouncementsApi";
import { AppIcon } from "../components/InstateamEmployeeComponents";

function formatDate(value) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function EmployeeAnnouncementsView({ token, searchText = "" }) {
  const [announcements, setAnnouncements] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  const fetchAnnouncements = async () => {
    if (!token) return;

    try {
      setIsLoading(true);
      setErrorMessage("");
      const payload = await getEmployeeAnnouncements(token);
      setAnnouncements(payload.announcements || []);
      setMetrics(payload.metrics || null);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const stats = useMemo(() => {
    const total = metrics?.total ?? announcements.length;
    const pinned = metrics?.pinned ?? announcements.filter((item) => item.pinned).length;
    const unread = metrics?.unread ?? announcements.filter((item) => !item.isRead).length;
    const acknowledged =
      metrics?.acknowledged ?? announcements.filter((item) => item.isAcknowledged).length;

    return [
      { label: "Total Announcements", value: total, note: "Visible to you", icon: "megaphone", tone: "yellow" },
      { label: "Pinned", value: pinned, note: "Important", icon: "pin", tone: "yellow" },
      { label: "Unread", value: unread, note: "Need attention", icon: "eye", tone: "yellow" },
      { label: "Acknowledged", value: acknowledged, note: "Confirmed by you", icon: "checkCircle", tone: "green" },
    ];
  }, [announcements, metrics]);

  const visibleAnnouncements = useMemo(() => {
    const query = String(searchText || "").trim().toLowerCase();
    if (!query) return announcements;
    return announcements.filter((announcement) =>
      [
        announcement.title,
        announcement.description,
        announcement.type,
        announcement.status,
        announcement.audience,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [announcements, searchText]);

  const handleRead = async (announcement, acknowledge = false) => {
    try {
      const payload = await markAnnouncementRead(token, announcement._id, acknowledge);
      const nextAnnouncement = payload.announcement;
      setAnnouncements((current) => {
        const nextAnnouncements = current.map((item) =>
          item._id === nextAnnouncement._id ? nextAnnouncement : item
        );

        setMetrics({
          total: nextAnnouncements.length,
          pinned: nextAnnouncements.filter((item) => item.pinned).length,
          unread: nextAnnouncements.filter((item) => !item.isRead).length,
          acknowledged: nextAnnouncements.filter((item) => item.isAcknowledged).length,
        });

        return nextAnnouncements;
      });
      setSelectedAnnouncement((current) =>
        current?._id === nextAnnouncement._id ? nextAnnouncement : current
      );
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const handleView = async (announcement) => {
    setSelectedAnnouncement(announcement);
    if (!announcement.isRead) {
      await handleRead(announcement, false);
    }
  };

  return (
    <section className="employee-portal-page employee-announcements-page">
      <div className="employee-page-heading">
        <h1>Announcements</h1>
        <div className="employee-breadcrumb">
          <span>Dashboard</span>
          <span>&gt;</span>
          <span>Announcements</span>
        </div>
      </div>

      <div className="employee-announcement-stats employee-announcement-metric-row">
        {stats.map((stat) => (
          <article key={stat.label} className="employee-stat-card employee-announcement-stat-card">
            <div className={`employee-large-icon ${stat.tone}`}>
              <AppIcon name={stat.icon} />
            </div>
            <div>
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
              <p>{stat.note}</p>
            </div>
          </article>
        ))}
      </div>

      {errorMessage ? <div className="employee-inline-alert">{errorMessage}</div> : null}

      <div className="employee-announcements-layout">
        <article className="employee-announcements-card">
          <div className="employee-table-title-row">
            <h2>Latest Announcements</h2>
            <span>{isLoading ? "Loading..." : "Live employee view"}</span>
          </div>

          <div className="employee-announcements-table">
            <div className="employee-announcements-header">
              <span>Title</span>
              <span>Type</span>
              <span>Date</span>
              <span>Reads</span>
              <span>Status</span>
              <span>Action</span>
            </div>

            {visibleAnnouncements.map((announcement) => (
              <div key={announcement._id} className="employee-announcement-row">
                <div className="employee-announcement-title-cell">
                  <div className="employee-small-icon">
                    <AppIcon name={announcement.pinned ? "pin" : "megaphone"} />
                  </div>
                  <div>
                    <strong>{announcement.pinned ? "Pinned: " : ""}{announcement.title}</strong>
                    <p>{announcement.description}</p>
                  </div>
                </div>
                <span className="employee-type-pill">{announcement.type}</span>
                <span>{formatDate(announcement.publishAt || announcement.createdAt)}</span>
                <span>{announcement.readCount || 0} read / {announcement.acknowledgedCount || 0} ack</span>
                <span
                  className={`employee-status-chip ${
                    announcement.isAcknowledged
                      ? "published"
                      : announcement.isRead
                        ? "pinned"
                        : "scheduled"
                  }`}
                >
                  {announcement.isAcknowledged
                    ? "Acknowledged"
                    : announcement.isRead
                      ? "Read"
                      : "Unread"}
                </span>
                <div className="employee-announcement-actions">
                  <button type="button" aria-label={`View ${announcement.title}`} onClick={() => handleView(announcement)}>
                    <AppIcon name="eye" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Acknowledge ${announcement.title}`}
                    disabled={announcement.isAcknowledged}
                    onClick={() => handleRead(announcement, true)}
                  >
                    <AppIcon name="checkCircle" />
                  </button>
                </div>
              </div>
            ))}

            {!visibleAnnouncements.length ? (
              <div className="employee-announcement-row">
                <div className="employee-announcement-title-cell">
                  <div className="employee-small-icon">
                    <AppIcon name="megaphone" />
                  </div>
                  <div>
                    <strong>{isLoading ? "Loading announcements..." : "No announcements found"}</strong>
                    <p>Company updates will appear here when your admin publishes them.</p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </article>

        <aside className="employee-announcement-side">
          {selectedAnnouncement ? (
            <InfoPanel
              icon={selectedAnnouncement.pinned ? "pin" : "megaphone"}
              title={selectedAnnouncement.title}
              text={selectedAnnouncement.description}
              tone={selectedAnnouncement.pinned ? "yellow" : "blue"}
            />
          ) : (
            <InfoPanel
              icon="pin"
              title="Pinned Notice"
              text="Important admin updates remain pinned at the top until acknowledged."
              tone="yellow"
            />
          )}
          <InfoPanel
            icon="users"
            title="Audience Filter"
            text="You only see announcements meant for you, your department, or all employees."
            tone="blue"
          />
          <InfoPanel
            icon="eye"
            title="Read Tracking"
            text="Opening or acknowledging an announcement updates the admin dashboard."
            tone="yellow"
          />
        </aside>
      </div>
    </section>
  );
}

function InfoPanel({ icon, title, text, tone }) {
  return (
    <article className="employee-info-panel">
      <div className={`employee-large-icon ${tone}`}>
        <AppIcon name={icon} />
      </div>
      <div>
        <h3>{title}</h3>
        <p>{text}</p>
      </div>
    </article>
  );
}
